import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { LYRIC_TEXT_COLORS } from '../constants/constants'
import themes from '../components/themes'
import {
  InvitesFromMap,
  InviteInfoMap,
  ModeType,
  Prefs,
  UserMatchesInfo,
  VizType,
  TrackIndex,
  PlayerVisibility,
} from '../types'

export const currentPrefsVersion = 8

export interface SessionState {
  isInitialized: boolean
  isRealtimeInitialized: boolean
  trackError: string
  matches: UserMatchesInfo
  matchInvites: InvitesFromMap
  challengeInfo: InviteInfoMap
  username: string
  prefs: Prefs
  currentTrackPath: string
  currentMatchSlug: string
  publicPlaylists: []
  isMulti: boolean
  mode: ModeType
  isShowCountdownClock: boolean
  countdownSeconds: number
  visualizationInfo: {
    defaultVizSlug: VizType
    currentVizSlug: VizType
    alternateVizSlug: VizType
  }[]
  isShowSocial: boolean
  isShowGameSidebar: boolean
  isShowSettings: boolean
  userAliases: string[]
  currUserAliasIndex: number
  spotifyToken: string
  spotifyPlayerState: null
  currentBackgroundIndex: number
  currentThemeIndex: number
  currentBackgroundBlendMode: number
  currentLyricsColorIndex: number
  isFullscreen: boolean
  isLandscape: boolean
  playerVisibility: PlayerVisibility
  trackIndex: TrackIndex
}

const initialState: SessionState = {
  isInitialized: false,
  isRealtimeInitialized: false,
  trackError: '',
  matches: { active: null, archived: [] },
  matchInvites: {},
  challengeInfo: {},
  username: '',
  prefs: {
    version: currentPrefsVersion,
    quickStart: {
      isShowOnStartup: true,
    },
    termsOfUse: {
      userHasAgreed: false,
    },
    theme: 0,
    background: 'track',
    audience: 'solo',
    defaultMode: 'viz',
    latencyMillis: 0,
    isUseLatency: true,
    lastTrackUri: '',
    scoreVersion: 2,
  },
  currentTrackPath: '',
  currentMatchSlug: '',
  publicPlaylists: [],
  isShowCountdownClock: false,
  countdownSeconds: 0,
  isMulti: false,
  mode: 'play',
  visualizationInfo: [
    { defaultVizSlug: 'page', alternateVizSlug: 'spiral', currentVizSlug: 'page' },
    { defaultVizSlug: 'page', alternateVizSlug: 'spiral', currentVizSlug: 'page' },
  ],
  isShowSocial: false,
  isShowGameSidebar: true,
  isShowSettings: false,
  userAliases: [],
  currUserAliasIndex: 0,
  spotifyToken: '',
  spotifyPlayerState: null,
  currentBackgroundIndex: 0,
  currentBackgroundBlendMode: 0,
  currentThemeIndex: 0,
  currentLyricsColorIndex: 0,
  isFullscreen: false,
  isLandscape: false,
  playerVisibility: {
    isShowGuestPlayers: true,
    isShowUserPlayers: false,
    isShowHostPlayer: false,
  },
  trackIndex: {},
}

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    initSession: (state, action) => {
      return {
        ...state,
        ...action.payload,
      }
    },
    setPrefs: (state, action) => {
      return {
        ...state,
        prefs: action.payload,
      }
    },
    setCurrentTrackPath: (state, action: PayloadAction<string>) => {
      state.currentTrackPath = action.payload
    },
    setTrackError: (state, action: PayloadAction<string>) => {
      state.trackError = action.payload
    },
    setCurrentBackgroundIndex: (state, action: PayloadAction<number>) => {
      state.currentBackgroundIndex = action.payload
    },
    setCurrentThemeIndex: (state, action: PayloadAction<number>) => {
      const newThemeIndex = action.payload
      if (0 <= newThemeIndex && newThemeIndex < themes.length) {
        state.currentThemeIndex = newThemeIndex
      } else {
        console.log(`ignoring bad theme index: [${newThemeIndex}]`)
      }
    },
    setCurrentBackgroundBlendMode: (state, action: PayloadAction<number>) => {
      state.currentBackgroundBlendMode = action.payload
    },
    setLyricsColorIndex: (state, action: PayloadAction<number>) => {
      state.currentLyricsColorIndex = action.payload
    },
    setLyricsColorIndexByName: (state, action: PayloadAction<string>) => {
      const newColorIndex = LYRIC_TEXT_COLORS.findIndex((color) => {
        return color.name === action.payload
      })
      if (newColorIndex < 0) {
        console.log(`[${action.payload}] not found in LYRIC_TEXT_COLORS`)
      } else {
        state.currentLyricsColorIndex = newColorIndex
      }
    },
    setCurrentVisualizationSlug: (
      state,
      action: PayloadAction<{ playerIndex: number; vizSlug: VizType }>
    ) => {
      const { playerIndex, vizSlug } = action.payload
      state.visualizationInfo[playerIndex].currentVizSlug = vizSlug
    },
    setDefaultVisualizationSlug: (
      state,
      action: PayloadAction<{ playerIndex: number; vizSlug: VizType }>
    ) => {
      const { playerIndex, vizSlug } = action.payload
      state.visualizationInfo[playerIndex].defaultVizSlug = vizSlug
    },
    setCurrentMatchSlug: (state, action: PayloadAction<string>) => {
      state.currentMatchSlug = action.payload
    },
    setMode: (state, action: PayloadAction<ModeType>) => {
      state.mode = action.payload
    },
    setIsShowCountdownClock: (state, action: PayloadAction<boolean>) => {
      state.isShowCountdownClock = action.payload
    },
    setCountdownSeconds: (state, action: PayloadAction<number>) => {
      state.countdownSeconds = action.payload
    },
    setIsMulti: (state, action: PayloadAction<boolean>) => {
      state.isMulti = action.payload
    },
    setMatches: (state, action) => {
      state.matches = action.payload
    },
    setActiveMatches: (state, action) => {
      state.matches.active = action.payload
    },
    setMatchInvites: (state, action) => {
      state.matchInvites = action.payload
    },
    setChallengeInfo: (state, action) => {
      state.challengeInfo = action.payload
    },
    setIsShowSocial: (state, action: PayloadAction<boolean>) => {
      state.isShowSocial = action.payload
    },
    setIsShowGameSidebar: (state, action: PayloadAction<boolean>) => {
      state.isShowGameSidebar = action.payload
    },
    setIsShowSettings: (state, action: PayloadAction<boolean>) => {
      state.isShowSettings = action.payload
    },
    setSpotifyToken: (state, action: PayloadAction<string>) => {
      state.spotifyToken = action.payload || ''
    },
    setSpotifyPlayerState: (state, action) => {
      state.spotifyPlayerState = action.payload
    },
    setIsFullscreen: (state, action: PayloadAction<boolean>) => {
      state.isFullscreen = action.payload
    },
    setIsLandscape: (state, action: PayloadAction<boolean>) => {
      state.isLandscape = action.payload
    },
    setPlayerVisibility: (state, action) => {
      state.playerVisibility = action.payload
    },
    setTrackIndex: (state, action: PayloadAction<TrackIndex>) => {
      state.trackIndex = action.payload
    },
  },
})

export default sessionSlice
